import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

export const getBloodAnalysisResultById = createAsyncThunk(
  "bloodAnalysisResult/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `/api/dog/testna_postaja/krvnaAnaliza/rezultat/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createBloodAnalysisResult = createAsyncThunk(
  "bloodAnalysisResult/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(
        "/api/dog/testna_postaja/krvnaAnaliza/rezultat",
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateBloodAnalysisResult = createAsyncThunk(
  "bloodAnalysisResult/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(
        `/api/dog/testna_postaja/krvnaAnaliza/rezultat/${id}`,
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteBloodAnalysisResult = createAsyncThunk(
  "bloodAnalysisResult/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.delete(
        `/api/dog/testna_postaja/krvnaAnaliza/rezultat/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getBloodAnalysisResultsPage = createAsyncThunk(
  "bloodAnalysisResult/getPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/dog/testna_postaja/krvnaAnaliza/rezultat/page",
        undefined,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  list: null,
  current: null,
  page: null,
  error: null,
  isLoading: false,
};

const bloodAnalysisResultSlice = createSlice({
  name: "bloodAnalysisResult",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrent: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get blood analysis result by ID
      .addCase(getBloodAnalysisResultById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBloodAnalysisResultById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload;
      })
      .addCase(getBloodAnalysisResultById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create blood analysis result
      .addCase(createBloodAnalysisResult.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBloodAnalysisResult.fulfilled, (state, action) => {
        state.isLoading = false;
        // POST endpoints return ApiResponseDto with output property
        state.current = action.payload.output;
      })
      .addCase(createBloodAnalysisResult.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update blood analysis result
      .addCase(updateBloodAnalysisResult.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBloodAnalysisResult.fulfilled, (state) => {
        state.isLoading = false;
        // PUT returns void, so we don't update state
      })
      .addCase(updateBloodAnalysisResult.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete blood analysis result
      .addCase(deleteBloodAnalysisResult.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBloodAnalysisResult.fulfilled, (state) => {
        state.isLoading = false;
        // DELETE returns ApiResponseDto, we don't update state
      })
      .addCase(deleteBloodAnalysisResult.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get paginated blood analysis results
      .addCase(getBloodAnalysisResultsPage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBloodAnalysisResultsPage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })
      .addCase(getBloodAnalysisResultsPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrent } = bloodAnalysisResultSlice.actions;
export default bloodAnalysisResultSlice.reducer;
